/**
 * Industry baseline templates per technology (FA pack, FA4).
 *
 * Pre-populated CAPEX / OPEX / financing values per technology so a
 * user without a financial model can fill the form by clicking
 * "SMR — First-of-a-kind" and starting from a sane baseline.
 *
 * Source: NREL ATB 2024 + Lazard LCOE v17.0 + EIA AEO 2024. Citation
 * strings on each baseline are surfaced as `provenance.baselineRef`
 * chips on the form so the user always knows where the number came
 * from. Edits flip the chip to `manual`.
 *
 * Discipline: every field that's null in the baseline means "we don't
 * have a defensible industry value, please enter manually". E.g.
 * decommissioning cost varies enough by jurisdiction that publishing
 * a single number would mislead.
 */
import type {
  FieldProvenance,
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialField,
} from '../../types/financialAssumptions';
import { emptyFinancialAssumptions } from '../../types/financialAssumptions';
import type { DataSource } from '../../types/siteRag';

export type BaselineKey =
  | 'smr-foak'
  | 'smr-noak'
  | 'solar-bess'
  | 'wind-bess'
  | 'gas-cc'
  | 'gas-sc'
  | 'hybrid';

export interface PublishedLcoeReference {
  /** Primary-source low end, USD/MWh. */
  lowUsdPerMwh: number;
  /** Primary-source high end, USD/MWh. */
  highUsdPerMwh: number;
  /** Primary source citation string. */
  primarySource: string;
  /** Primary source URL for traceable audit verification. */
  sourceUrl: string;
  /** Vintage of the underlying primary dataset. */
  vintage: string;
  /** Methodology summary (e.g., DCF / ATB / LCOE+ unsubsidized busbar). */
  methodology: string;
  /** Real vs. nominal currency basis and reference dollar year. */
  currencyBasis: string;
  /** Financing assumptions (WACC, debt/equity split, or sponsor-dependent). */
  financingBasis: string;
  /** Tax credit / subsidy status in the published range. */
  subsidyStatus: 'unsubsidized' | 'subsidized';
  /** Deployment maturity scenario (FOAK vs. NOAK vs. commercial fleet). */
  maturityScenario: 'FOAK' | 'NOAK' | 'commercial-fleet';
  /** Firming / dispatch scope (e.g. standalone 4-hr storage vs. 24/7 firmed baseload). */
  firmingScope:
    | 'busbar-baseload'
    | 'busbar-dispatchable'
    | 'standalone-4hr-storage'
    | 'standalone-storage'
    | 'peaking-only';
  /** Explicit warning against treating this reference as a site estimate or interchangeable 24/7 firmed-load cost. */
  comparisonCaveat: string;
}

export interface BaselineTemplate {
  /** Stable lookup key. */
  key: BaselineKey;
  /** Human-readable label shown in the tech-picker modal. */
  label: string;
  /** One-line description for the tech-picker card. */
  description: string;
  /** Source citation — rendered in the provenance chip. */
  source: string;
  /**
   * P3-8 — vintage of the cited dataset (ISO year or YYYY-MM). Surfaced as a
   * staleness chip so a user can see HOW OLD the baseline is (the audit flagged
   * "2-vintage-stale 2024 with no surfaced staleness"). `getBaselineStaleness`
   * derives a stale/current flag from this against the current year.
   */
  asOf: string;
  /**
   * Traceable primary-source LCOE reference range and provenance metadata.
   * These are comparison references (not site estimates or interchangeable
   * 24/7 firmed-load costs) and are never used to seed a single-point
   * `lcoeNominalPerMwhUsd` average into a project model.
   */
  publishedReferenceLcoe?: PublishedLcoeReference;
  /**
   * Field values. `null` means "no defensible industry value — leave
   * the field empty for manual entry". Fields not listed are also
   * left as the empty-state null.
   */
  values: Partial<FinancialAssumptionsCore>;
}

/** P3-8 — number of vintages (years) before a cost baseline is "stale". */
export const BASELINE_STALE_AFTER_YEARS = 1;

/**
 * P3-8 — derive a per-baseline staleness verdict from its `asOf` vintage.
 * Returns the parsed vintage year, the age in years, and a `stale` flag
 * (true when the baseline is more than `BASELINE_STALE_AFTER_YEARS` old).
 * Pure; `nowYear` is injectable for deterministic tests.
 */
export function getBaselineStaleness(
  baseline: Pick<BaselineTemplate, 'asOf'>,
  nowYear: number = new Date().getUTCFullYear(),
): { vintageYear: number | null; ageYears: number | null; stale: boolean } {
  const m = /^(\d{4})/.exec(baseline.asOf.trim());
  if (!m) return { vintageYear: null, ageYears: null, stale: false };
  const vintageYear = Number(m[1]);
  const ageYears = nowYear - vintageYear;
  return { vintageYear, ageYears, stale: ageYears > BASELINE_STALE_AFTER_YEARS };
}

/**
 * Baseline catalogue. Numbers cite NREL ATB 2024 + Lazard LCOE v17.0
 * unless otherwise noted. Updated quarterly when the underlying
 * datasets refresh.
 */
export const INDUSTRY_BASELINES: Record<BaselineKey, BaselineTemplate> = {
  'smr-foak': {
    key: 'smr-foak',
    label: 'SMR — First-of-a-kind',
    description: 'Light-water small modular reactor; first commercial deployment in fleet (FOAK).',
    source: 'EPRI 2024 Report 3002029428 (2023 unsubsidized SMR FOAK $111–$143/MWh, 2023$ real) + NREL ATB 2024 (SMR FOAK) — https://restservice.epri.com/publicdownload/000000003002029428/0/Product',
    asOf: '2024',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 111,
      highUsdPerMwh: 143,
      primarySource: 'EPRI 2024 Report 3002029428 (Advanced Nuclear Technology: 2023 Unsubsidized SMR FOAK)',
      sourceUrl: 'https://restservice.epri.com/publicdownload/000000003002029428/0/Product',
      vintage: '2024 (2023 cost basis)',
      methodology: 'Levelized busbar cost across first-of-a-kind SMR deployment assumptions',
      currencyBasis: '2023 USD real',
      financingBasis: 'Utility/merchant nuclear reference financing (sponsor WACC required for site model)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'FOAK',
      firmingScope: 'busbar-baseload',
      comparisonCaveat: 'Comparison reference only (2023 unsubsidized FOAK $111–$143/MWh vs. projected 2035 NOAK $94–$121/MWh under different learning-curve assumptions); not a site-specific estimate.',
    },
    values: {
      technology: 'smr',
      capacityMW: 250,
      capacityFactorPct: 92,
      projectLifeYears: 60,
      constructionMonths: 60,
      capexPerKwUsd: 6500,
      capexTotalUsd: 250 * 1000 * 6500,        // capacityMW × kW/MW × $/kW
      fixedOmPerKwYearUsd: 115,
      variableOmPerMwhUsd: 2.5,
      fuelCostPerMwhUsd: 8.0,
      // Do not seed an unverified single-point LCOE average; LCOE is derived by recompute() once WACC/financing is entered.
      lcoeNominalPerMwhUsd: null,
      // Financing left null — sponsor-specific.
    },
  },
  'smr-noak': {
    key: 'smr-noak',
    label: 'SMR — Nth-of-a-kind',
    description: 'Same tech, post-FOAK learning curve (projected 2035 NOAK). Lower CAPEX, same OPEX.',
    source: 'EPRI 2024 Report 3002029428 (projected 2035 unsubsidized SMR NOAK $94–$121/MWh vs. 2023 FOAK $111–$143/MWh) + NREL ATB 2024 (SMR NOAK) — https://restservice.epri.com/publicdownload/000000003002029428/0/Product',
    asOf: '2024',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 94,
      highUsdPerMwh: 121,
      primarySource: 'EPRI 2024 Report 3002029428 (Projected 2035 Unsubsidized SMR NOAK; 2023 FOAK is $111–$143/MWh)',
      sourceUrl: 'https://restservice.epri.com/publicdownload/000000003002029428/0/Product',
      vintage: '2024 (projected 2035 NOAK vs. 2023 FOAK)',
      methodology: 'Levelized busbar cost assuming post-FOAK nth-of-a-kind learning curve by 2035',
      currencyBasis: '2023 USD real',
      financingBasis: 'Utility/merchant nuclear reference financing (sponsor WACC required for site model)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'NOAK',
      firmingScope: 'busbar-baseload',
      comparisonCaveat: 'Comparison reference only: distinguishes projected 2035 NOAK ($94–$121/MWh) from 2023 FOAK ($111–$143/MWh) under different assumptions; not a site estimate.',
    },
    values: {
      technology: 'smr',
      capacityMW: 250,
      capacityFactorPct: 92,
      projectLifeYears: 60,
      constructionMonths: 48,
      capexPerKwUsd: 4500,
      capexTotalUsd: 250 * 1000 * 4500,
      fixedOmPerKwYearUsd: 115,
      variableOmPerMwhUsd: 2.5,
      fuelCostPerMwhUsd: 8.0,
      lcoeNominalPerMwhUsd: null,
    },
  },
  'solar-bess': {
    key: 'solar-bess',
    label: 'Solar + BESS (utility-scale)',
    description: 'Bifacial PV + 4-hr lithium-ion battery storage, single-axis tracker.',
    source: 'Lazard LCOE v17.0 (June 2024, unsubsidized utility solar + storage $60–$210/MWh) + NREL Solar Futures Study DOE/EE-2491 — https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
    asOf: '2024-06',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 60,
      highUsdPerMwh: 210,
      primarySource: 'Lazard LCOE+ v17.0 (June 2024, Unsubsidized Utility-Scale Solar + Storage)',
      sourceUrl: 'https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
      vintage: '2024-06',
      methodology: '20-year DCF busbar LCOE for standalone utility-scale PV paired with 4-hour Li-ion BESS',
      currencyBasis: '2024 USD nominal',
      financingBasis: 'After-tax WACC 8.64% at 20% combined tax rate (60% debt at 8.0%, 40% equity at 12.0%; 7.7% [7.68%] at Lazard 40% combined tax rate)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'commercial-fleet',
      firmingScope: 'standalone-4hr-storage',
      comparisonCaveat: 'Comparison reference only: standalone PV + 4-hr storage ($60–$210/MWh), NOT interchangeable with 24/7 firmed data-center baseload (which requires multi-day firming and 3–5x generation overbuild).',
    },
    values: {
      technology: 'solar-bess',
      capacityMW: 200,
      capacityFactorPct: 28,
      projectLifeYears: 35,
      constructionMonths: 18,
      capexPerKwUsd: 1450,
      capexTotalUsd: 200 * 1000 * 1450,
      fixedOmPerKwYearUsd: 16,
      variableOmPerMwhUsd: 0,
      fuelCostPerMwhUsd: null,
      lcoeNominalPerMwhUsd: null,
    },
  },
  'wind-bess': {
    key: 'wind-bess',
    label: 'Onshore Wind + BESS',
    description: 'Class 4 onshore wind + battery storage.',
    source: 'Lazard LCOE v17.0 (June 2024, unsubsidized onshore wind + storage $45–$133/MWh) + NREL ATB 2024 (Wind ONSHORE) — https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
    asOf: '2024-06',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 45,
      highUsdPerMwh: 133,
      primarySource: 'Lazard LCOE+ v17.0 (June 2024, Unsubsidized Onshore Wind + Storage)',
      sourceUrl: 'https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
      vintage: '2024-06',
      methodology: '20-year DCF busbar LCOE for standalone onshore wind paired with battery storage',
      currencyBasis: '2024 USD nominal',
      financingBasis: 'After-tax WACC 8.64% at 20% combined tax rate (60% debt at 8.0%, 40% equity at 12.0%; 7.7% [7.68%] at Lazard 40% combined tax rate)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'commercial-fleet',
      firmingScope: 'standalone-storage',
      comparisonCaveat: 'Comparison reference only: standalone onshore wind + storage ($45–$133/MWh), NOT interchangeable with 24/7 firmed data-center baseload.',
    },
    values: {
      technology: 'wind-bess',
      capacityMW: 200,
      capacityFactorPct: 38,
      projectLifeYears: 30,
      constructionMonths: 24,
      capexPerKwUsd: 1700,
      capexTotalUsd: 200 * 1000 * 1700,
      fixedOmPerKwYearUsd: 38,
      variableOmPerMwhUsd: 0,
      fuelCostPerMwhUsd: null,
      lcoeNominalPerMwhUsd: null,
    },
  },
  'gas-cc': {
    key: 'gas-cc',
    label: 'Natural gas combined cycle',
    description: 'F-class CCGT, base-load operation, no CCUS.',
    source: 'Lazard LCOE v17.0 (June 2024, unsubsidized gas combined cycle $45–$108/MWh) + EIA AEO 2024 — https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
    asOf: '2024-06',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 45,
      highUsdPerMwh: 108,
      primarySource: 'Lazard LCOE+ v17.0 (June 2024, Unsubsidized Gas Combined Cycle) + EIA AEO 2024',
      sourceUrl: 'https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
      vintage: '2024-06',
      methodology: '20-year DCF busbar LCOE at $3.45/MMBtu reference gas price (excluding site pipeline lateral and firming redundancy)',
      currencyBasis: '2024 USD nominal',
      financingBasis: 'After-tax WACC 8.64% at 20% combined tax rate (60% debt at 8.0%, 40% equity at 12.0%; 7.7% [7.68%] at Lazard 40% combined tax rate)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'commercial-fleet',
      firmingScope: 'busbar-dispatchable',
      comparisonCaveat: 'Comparison reference only ($45–$108/MWh busbar); excludes site-specific gas lateral, firm transport reservation, and N+1 redundancy.',
    },
    values: {
      technology: 'gas-cc',
      capacityMW: 600,
      capacityFactorPct: 85,
      projectLifeYears: 30,
      constructionMonths: 36,
      capexPerKwUsd: 1100,
      capexTotalUsd: 600 * 1000 * 1100,
      fixedOmPerKwYearUsd: 14,
      variableOmPerMwhUsd: 2.0,
      fuelCostPerMwhUsd: 28.0,
      lcoeNominalPerMwhUsd: null,
    },
  },
  'gas-sc': {
    key: 'gas-sc',
    label: 'Natural gas simple cycle (peaker)',
    description: 'Aeroderivative or F-class peaker, 10-15% capacity factor.',
    source: 'Lazard LCOE v17.0 (June 2024, unsubsidized gas peaking $110–$228/MWh) + EIA AEO 2024 — https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
    asOf: '2024-06',
    publishedReferenceLcoe: {
      lowUsdPerMwh: 110,
      highUsdPerMwh: 228,
      primarySource: 'Lazard LCOE+ v17.0 (June 2024, Unsubsidized Gas Peaking) + EIA AEO 2024',
      sourceUrl: 'https://www.lazard.com/media/xemfey0k/lazards-lcoeplus-june-2024-_vf.pdf',
      vintage: '2024-06',
      methodology: '20-year DCF busbar LCOE at 10% peaking capacity factor',
      currencyBasis: '2024 USD nominal',
      financingBasis: 'After-tax WACC 8.64% at 20% combined tax rate (60% debt at 8.0%, 40% equity at 12.0%; 7.7% [7.68%] at Lazard 40% combined tax rate)',
      subsidyStatus: 'unsubsidized',
      maturityScenario: 'commercial-fleet',
      firmingScope: 'peaking-only',
      comparisonCaveat: 'Peaking capacity reference only (10–15% CF); not continuous 24/7 baseload generation.',
    },
    values: {
      technology: 'gas-sc',
      capacityMW: 200,
      capacityFactorPct: 12,
      projectLifeYears: 30,
      constructionMonths: 18,
      capexPerKwUsd: 850,
      capexTotalUsd: 200 * 1000 * 850,
      fixedOmPerKwYearUsd: 18,
      variableOmPerMwhUsd: 5.0,
      fuelCostPerMwhUsd: 32.0,
      lcoeNominalPerMwhUsd: null,
    },
  },
  'hybrid': {
    key: 'hybrid',
    label: 'Hybrid (mixed generation)',
    description: 'Multi-asset project; start with empty fields and fill from your model.',
    source: 'No defensible industry baseline — mixed-asset projects vary too widely',
    asOf: '2024',
    values: {
      technology: 'mixed',
      lcoeNominalPerMwhUsd: null,
    },
  },
};

/**
 * Apply a baseline to a FinancialAssumptions, stamping
 * `industry-baseline` provenance on each populated field. Used by
 * the FA3 picker after the user picks a tech.
 *
 * Discipline:
 *   - Returns a new FinancialAssumptions; does not mutate the input.
 *   - Skips fields the baseline has set to null.
 *   - Does NOT overwrite fields that already have non-null values on
 *     `existing` — the user's prior edits win. The form's "apply
 *     baseline" button can call `applyBaselineForce` instead when the
 *     user explicitly wants to overwrite.
 */
export function applyBaseline(
  existing: FinancialAssumptions | null,
  key: BaselineKey,
  now: string,
  updatedBy: string,
): FinancialAssumptions {
  const baseline = INDUSTRY_BASELINES[key];
  const fa = existing ?? emptyFinancialAssumptions(now, updatedBy);
  const nextProvenance = { ...fa.fieldProvenance };
  const updates: Record<string, unknown> = {};
  const provenance: FieldProvenance = {
    source: 'industry-baseline',
    confidence: 'medium',
    baselineRef: baseline.source,
    extractedAt: now,
  };
  for (const [field, value] of Object.entries(baseline.values)) {
    if (value == null) continue;
    const existingValue = (fa as unknown as Record<string, unknown>)[field];
    if (existingValue != null && existingValue !== '') {
      // Don't overwrite user-entered values silently.
      continue;
    }
    updates[field] = value;
    nextProvenance[field as FinancialField] = provenance;
  }
  return {
    ...fa,
    ...(updates as Partial<FinancialAssumptions>),
    fieldProvenance: nextProvenance,
    source: 'industry-baseline',
    updatedAt: now,
    updatedBy,
  };
}

/**
 * Force-apply variant — overwrites whatever's already there. Used by
 * the form's explicit "Reset to baseline" affordance.
 */
export function applyBaselineForce(
  existing: FinancialAssumptions | null,
  key: BaselineKey,
  now: string,
  updatedBy: string,
): FinancialAssumptions {
  const baseline = INDUSTRY_BASELINES[key];
  const fa = existing ?? emptyFinancialAssumptions(now, updatedBy);
  const nextProvenance = { ...fa.fieldProvenance };
  const updates: Record<string, unknown> = {};
  const provenance: FieldProvenance = {
    source: 'industry-baseline',
    confidence: 'medium',
    baselineRef: baseline.source,
    extractedAt: now,
  };
  for (const [field, value] of Object.entries(baseline.values)) {
    if (value == null) continue;
    updates[field] = value;
    nextProvenance[field as FinancialField] = provenance;
  }
  return {
    ...fa,
    ...(updates as Partial<FinancialAssumptions>),
    fieldProvenance: nextProvenance,
    source: 'industry-baseline',
    updatedAt: now,
    updatedBy,
  };
}

/**
 * Per-technology "typical range" for the form's outlier-highlight
 * affordance. Values >50% outside this band get the amber chip in
 * the form (FA3). Each tuple is `[low, high]` in the field's
 * canonical units. Ranges keyed by canonical technology (not
 * baseline key) since they cover both FOAK and NOAK.
 */
export const TYPICAL_RANGES: Record<
  FinancialAssumptionsCore['technology'],
  Partial<Record<FinancialField, [number, number]>>
> = {
  'smr': {
    capexPerKwUsd: [4000, 9000],
    fixedOmPerKwYearUsd: [80, 150],
    variableOmPerMwhUsd: [1.5, 4.0],
    fuelCostPerMwhUsd: [5.0, 12.0],
    capacityFactorPct: [85, 95],
    projectLifeYears: [40, 80],
    // EPRI 2024 (3002029428): projected 2035 NOAK $94–$121/MWh to 2023 FOAK $111–$143/MWh.
    lcoeNominalPerMwhUsd: [94, 143],
  },
  'gas-cc': {
    capexPerKwUsd: [900, 1400],
    fixedOmPerKwYearUsd: [10, 20],
    variableOmPerMwhUsd: [1.5, 3.0],
    fuelCostPerMwhUsd: [20, 50],
    capacityFactorPct: [50, 90],
    projectLifeYears: [25, 35],
    // Lazard LCOE+ v17.0 (June 2024): unsubsidized gas combined cycle $45–$108/MWh.
    lcoeNominalPerMwhUsd: [45, 108],
  },
  'gas-sc': {
    capexPerKwUsd: [700, 1100],
    fixedOmPerKwYearUsd: [12, 24],
    variableOmPerMwhUsd: [3, 8],
    fuelCostPerMwhUsd: [20, 50],
    capacityFactorPct: [5, 25],
    projectLifeYears: [25, 35],
    // Lazard LCOE+ v17.0 (June 2024): unsubsidized gas peaking $110–$228/MWh.
    lcoeNominalPerMwhUsd: [110, 228],
  },
  'solar-bess': {
    capexPerKwUsd: [1100, 1800],
    fixedOmPerKwYearUsd: [12, 22],
    variableOmPerMwhUsd: [0, 1],
    capacityFactorPct: [22, 35],
    projectLifeYears: [25, 40],
    // Lazard LCOE+ v17.0 (June 2024): unsubsidized utility solar + storage $60–$210/MWh.
    lcoeNominalPerMwhUsd: [60, 210],
  },
  'wind-bess': {
    capexPerKwUsd: [1400, 2200],
    fixedOmPerKwYearUsd: [30, 50],
    variableOmPerMwhUsd: [0, 2],
    capacityFactorPct: [30, 50],
    projectLifeYears: [25, 35],
    // Lazard LCOE+ v17.0 (June 2024): unsubsidized onshore wind + storage $45–$133/MWh.
    lcoeNominalPerMwhUsd: [45, 133],
  },
  'mixed': {},
  'tbd': {},
};

/**
 * Returns `true` when `value` is >50% outside the typical range for
 * the field + technology. Used by the form's outlier-chip logic.
 *
 * Returns `false` (not an outlier) when:
 *   - value is null or NaN
 *   - technology is `tbd` or `mixed` (no defensible range)
 *   - we have no range entry for this field on this tech
 */
export function isOutlier(
  technology: FinancialAssumptionsCore['technology'],
  field: FinancialField,
  value: number | null,
): boolean {
  if (value == null || !Number.isFinite(value)) return false;
  const ranges = TYPICAL_RANGES[technology];
  if (!ranges) return false;
  const band = ranges[field];
  if (!band) return false;
  const [low, high] = band;
  const lowerBound = low * 0.5;
  const upperBound = high * 1.5;
  return value < lowerBound || value > upperBound;
}

/**
 * v1.3.0 APPENDIX LCOE-SOURCES — the baseline keys whose `source`
 * citation strings actually back the report's LCOE chart + financial
 * model.
 *
 * These mirror EXACTLY the catalogue entries `LcoeChartSection`'s
 * `BUCKET_BASELINE_KEY` consumes for the published industry-norm LCOE
 * bands (nuclear → smr-noak, gas → gas-cc, solar → solar-bess,
 * wind → wind-bess). They are the four real LCOE datasets — NREL ATB
 * 2024 / Lazard LCOE v17.0 / EIA AEO 2024 / NREL Solar Futures / EPRI —
 * that the financial-model tech-picker seeds from and the LCOE chart
 * cites in its footnote.
 *
 * Deliberately EXCLUDES:
 *   - `smr-foak` — the FOAK premium variant; the chart uses the NOAK
 *     steady-state figure, so its source (same NREL ATB + EPRI) is
 *     already represented by `smr-noak` after dedup. Listing both would
 *     duplicate the citation string.
 *   - `hybrid` — its `source` is the non-source sentinel "No defensible
 *     industry baseline …", which is not a citable dataset.
 */
const LCOE_SOURCE_BASELINE_KEYS: readonly BaselineKey[] = [
  'smr-noak',
  'gas-cc',
  'solar-bess',
  'wind-bess',
];

/**
 * Substrings the appendix render filter (`ReferencesSection.tsx`) strips
 * from `report.references` — an "unavailable / failed / default-estimate"
 * source never reaches the Data Sources & References list. We pre-screen
 * here so the producer never emits a row the renderer would silently
 * drop. Lower-cased compare to match the renderer's `.toLowerCase()`.
 */
const STRIPPED_SOURCE_SUBSTRINGS: readonly string[] = [
  'unavailable',
  'failed',
  'default estimate',
  // Guard the `hybrid` sentinel ("No defensible industry baseline …")
  // even though it's excluded by key above — defence in depth so a
  // future key addition can't leak a non-source string.
  'no defensible',
];

function isCitableBaselineSource(source: string): boolean {
  const s = source.toLowerCase();
  return !STRIPPED_SOURCE_SUBSTRINGS.some((sub) => s.includes(sub));
}

/**
 * v1.3.0 APPENDIX LCOE-SOURCES — project the INDUSTRY_BASELINES `source`
 * citation strings (NREL ATB 2024 / Lazard LCOE v17.0 / EIA AEO 2024 /
 * NREL Solar Futures / EPRI) into `DataSource` reference rows so the
 * report appendix's "Data Sources & References" list carries the
 * LCOE / financial-model data sources too.
 *
 * Discipline:
 *   - NO FABRICATION — every row's `source` is an INDUSTRY_BASELINES
 *     entry verbatim. Only the four LCOE-chart baselines
 *     (`LCOE_SOURCE_BASELINE_KEYS`) are projected; the FOAK premium and
 *     the `hybrid` sentinel are excluded.
 *   - DEDUPED by `source` string so baselines that cite the same dataset
 *     collapse to a single appendix row (these four are distinct today,
 *     but the dedup is future-proof).
 *   - The `field` is `'benchmark'` so the appendix categorises the row
 *     as "Industry Benchmark" (`referenceCategoryLabels`). It renders in
 *     the flat ascending references register (#13, 2026-06-28) — the
 *     reviewer-mental-model buckets were retired in favour of one register.
 *   - Pre-screened against the appendix render filter so no row is
 *     emitted that the renderer would silently strip.
 *
 * Pure + deterministic; `now` is the producer's shared `retrievedAt`
 * stamp so the appendix's retrieved column stays internally consistent.
 */
export function buildIndustryBaselineReferenceRows(now: string): DataSource[] {
  const out: DataSource[] = [];
  const seenSources = new Set<string>();
  for (const key of LCOE_SOURCE_BASELINE_KEYS) {
    const baseline = INDUSTRY_BASELINES[key];
    const source = baseline.source?.trim();
    if (!source || !isCitableBaselineSource(source)) continue;
    if (seenSources.has(source)) continue;
    seenSources.add(source);
    out.push({ field: 'benchmark', source, retrievedAt: now });
  }
  return out;
}
